import { DateTextFilterCell } from "~/shared/components/trax-grid/date-text-filter-cell/date-text-filter-cell.tsx";
import { DebouncedTextFilterCell } from "~/shared/components/trax-grid/debounced-text-filter-cell/debounced-text-filter-cell.tsx";
import type { BoardColumn } from "~/shared/types/quote-board-types";

import { LoadDateCell } from "../components/load-date-cell/load-date-cell.tsx";
import {
	DeliveryLocationCell,
	PickupLocationCell,
} from "../components/location-cell/load-location-cells.tsx";
import { PoNumberCell } from "../components/po-number-cell/po-number-cell.tsx";
import { StatusCell } from "../components/status-cell/status-cell.tsx";

type BuildLoadDataColumnsArgs = {
	isInternal: boolean;
};

/**
 * A data column declaration owned by this file. `show` is intentionally
 * omitted — visibility is decided by `applyGridPrefs` using
 * `defaultShipmentBoardGridPrefs` and the user's saved prefs. `width` is
 * also omitted by default; the only column that declares it inline is the
 * pinned (`required`) one, because pinned columns bypass `applyGridPrefs`.
 */
export type LoadDataColumn = Omit<BoardColumn, "show" | "width"> & {
	width?: number;
};

/**
 * Builds the data columns (those with a `field` value) for the Loads grid.
 *
 * Source of truth for column **rendering** metadata only: `field`, `title`
 * (deployment-aware where needed), `menu`, `filterable`, `filterCell`,
 * custom `cells` renderers, and `required` / `reorderable`.
 *
 * Per-deployment **visibility**, default **show/hide**, **order**, and
 * **width** are owned by `defaultShipmentBoardGridPrefs` and merged with the
 * user's saved prefs by `applyGridPrefs` (in `useLoadColumns`).
 */
export const buildLoadDataColumns = ({
	isInternal,
}: BuildLoadDataColumnsArgs): LoadDataColumn[] => {
	const columns: LoadDataColumn[] = [
		{
			field: "rateType",
			title: "Mode",
			menu: true,
			filterable: true,
		},
		{
			field: "poNumber",
			title: "TQL PO #",
			menu: true,
			required: true,
			reorderable: false,
			filterable: true,
			// Pinned (required) columns bypass `applyGridPrefs`, so width must
			// be declared inline here.
			width: 100,
			cells: {
				data: PoNumberCell,
			},
		},
		{
			field: "formattedStatus",
			title: "Status",
			menu: true,
			filterable: true,
			cells: {
				data: StatusCell,
			},
		},
		{
			field: "customerName",
			title: isInternal ? "Billing Customer" : "Customer Name",
			menu: true,
			filterable: true,
		},
		{
			field: "formattedPickupAddress",
			title: isInternal ? "Pickup Address" : "Pickup Location",
			menu: true,
			filterable: true,
			cells: {
				data: PickupLocationCell,
			},
		},
		{
			field: "formattedDeliveryAddress",
			title: isInternal ? "Delivery Address" : "Delivery Location",
			menu: true,
			filterable: true,
			cells: {
				data: DeliveryLocationCell,
			},
		},
		{
			field: "formattedLastLocation",
			title: "Last Location",
			menu: true,
			filterable: true,
		},
		{
			field: isInternal ? "carrierName" : "ltlCarrierName",
			title: isInternal ? "Carrier Name" : "LTL Carrier Name",
			menu: true,
			filterable: true,
		},
		{
			field: "proNumber",
			title: "PRO Number",
			menu: true,
			filterable: true,
		},
		{
			// Bind to `loadDateValue` (a Date) — not the raw string `loadDate`
			// or `pickupDate` from the API — so click-to-sort produces correct
			// chronological order and the `DateTextFilterCell` operator receives
			// a real Date. Mirrors the delivery date column.
			field: "loadDateValue",
			title: isInternal ? "P/U Date" : "Pickup Date",
			menu: true,
			filterable: true,
			filterCell: DateTextFilterCell,
			cells: {
				data: LoadDateCell,
			},
		},
		{
			field: "deliveryDateValue",
			title: isInternal ? "Del. Date" : "Delivery Date",
			menu: true,
			filterable: true,
			filterCell: DateTextFilterCell,
			cells: {
				data: LoadDateCell,
			},
		},
		{
			field: "ltlArName",
			title: "LTL AR Name",
			menu: true,
			filterable: true,
		},
		{
			field: "customerPO",
			title: "Customer PO",
			menu: true,
			filterable: true,
		},
		{
			field: "pickupPOList",
			title: isInternal ? "P/U Number" : "Pickup Number",
			menu: true,
			filterable: true,
		},
		{
			field: "deliveryPOList",
			title: "Delivery Number",
			menu: true,
			filterable: true,
		},
		{
			field: "soNumber",
			title: "SO Number",
			menu: true,
			filterable: true,
		},
		{
			field: "quoteId",
			title: "TQL Quote ID",
			menu: true,
			filterable: true,
		},
		{
			field: "custRefNum",
			title: "Customer Reference",
			menu: true,
			filterable: true,
		},
		{
			field: "tenderedCustomerName",
			title: "Tendered Customer Name",
			menu: true,
			filterable: true,
		},
		{
			field: "ediStatus",
			title: "EDI Status",
			menu: true,
			filterable: true,
		},
		{
			field: "primaryBrokerName",
			title: "Broker Name",
			menu: true,
			filterable: true,
		},
		{
			field: "omassStatus",
			title: "OMASS Status",
			menu: true,
			filterable: true,
		},
		{
			field: "ftlGsm",
			title: "FTL GSM",
			menu: true,
			filterable: true,
		},
		{
			field: "serviceLevel",
			title: "Service Level (Guarantee)",
			menu: true,
			filterable: true,
		},
	];

	// Default every filterable text column to the auto-applying filter
	// cell so a cleared input immediately fires `onFilterChange` (Kendo's
	// default text cell only submits on Enter or via the filter button,
	// which means selecting+deleting+clicking-away never updates the
	// filter and the grid keeps showing previously-filtered rows).
	// Columns that already declare their own `filterCell` (e.g. the date
	// columns) are left untouched.
	return columns.map((col) =>
		col.filterable && !col.filterCell
			? { ...col, filterCell: DebouncedTextFilterCell }
			: col,
	);
};
